import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/database';
import { MatTableDataSource, MatTable } from '@angular/material/table';
import { BehaviorSubject, Observable } from 'rxjs';
import { Address } from '../models/address';
import { Parcel, Stop, Stops } from '../models/live-route';
import { LiveRouteStatus } from '../models/live-route-status';
import { ApiProvider } from '../providers/api.provider';
import { SectorService } from './sector.service';
import { AgmGoogleMapService } from './googlemap.service';
import { Polyline } from '../models/polyline';

@Injectable({
  providedIn: 'root'
})
export class StopService {
  public currentStopsSubject: BehaviorSubject<Stops>;
  public currentStops: Observable<Stops>;
  public dataSource: MatTableDataSource<Stop>;
  public minLiveRouteStatus: number = 0;
  public RouteID: number = 0;
  public RouteDate: Date = new Date();
  public StopCount: number = 0; //Commit
  public latitude: number = 45.500922;
  public longitude: number = -73.443143;
  public selectedRouteTotalDistance: number = 0;
  private currentParcel: Parcel;
  private currentStop: Stop;
  public loadingInProgress: BehaviorSubject<boolean>;
  public routeHasBeenLocked: BehaviorSubject<boolean>;
  public GetDataDone: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public HideETACalculation: boolean = false;

  public nonCompletedStops: Observable<Stops>;
  public nonCompletedStopsSubject: BehaviorSubject<Stops>;
  public ncDataSource: MatTableDataSource<Stop>;

  constructor(
    public apiProvider: ApiProvider,
    public datePipe: DatePipe,
    private zone: NgZone,
    private googleMapService: AgmGoogleMapService,
    public _sectorService: SectorService
  ) {
    this.currentStopsSubject = new BehaviorSubject<Stops>(null);
    this.loadingInProgress = new BehaviorSubject<boolean>(null);
    this.routeHasBeenLocked = new BehaviorSubject<boolean>(null);
    this.nonCompletedStopsSubject = new BehaviorSubject<Stops>(null);
    // this.currentStopsSubject.getValue().StopsList[0].ParcelCompress[0].No_Stop
  }

  public get currentStopsValue(): Stops {
    return this.currentStopsSubject.value;
  }

  public get getLoadingInProgress(): boolean {
    return this.loadingInProgress.value;
  }

  public get getRouteHasBeenLocked(): boolean {
    return this.routeHasBeenLocked.value;
  }

  public get getNonCompletedStops(): Stops {
    return this.nonCompletedStopsSubject.value;
  }

  public setRouteHasBeenLocked(value: boolean) {
    return this.routeHasBeenLocked.next(value);
  }

  public setLoadingInProgress(value: boolean) {
    this.loadingInProgress.next(value);
  }

  public get selectedStops(): Stop[] {
    return this.currentStopsValue.StopsList.filter(
      (x) => x.IsSelected === true
    );
  }

  public get selectedStopsAllowedException(): Stop[] {
    return this.selectedStops.filter((x) =>
      x.ParcelCompress.filter(
        (y) => y.Live_Route_Status === this.minLiveRouteStatus
      )
    );
  }
  /*
  public get visibleStops(): Stop[] {
    return this.currentStopsValue.StopsList.filter(x => (this.authenticationService.currentUserValue.FilterCompleted == 0 ||
      (this.authenticationService.currentUserValue.FilterCompleted == 1 && x.TotalScanned == x.ParcelCompress.length) ||
      (this.authenticationService.currentUserValue.FilterCompleted == 2 && x.TotalScanned != x.ParcelCompress.length) ));
  }
    */

  public setAddressStops(value: Stop) {
    if (this.dataSource && this.dataSource.data) {
      for (let entry of this.dataSource.data) {
        if (entry.Consol_ID === value.Consol_ID) {
          this.GetStop(entry.Consol_ID).NeedAddressCheck = true;
        }
      }
    }
  }

  public setUnservedStops(value: Stop) {
    if (this.dataSource && this.dataSource.data) {
      for (let entry of this.dataSource.data) {
        if (entry.Consol_ID === value.Consol_ID) {
          this.GetStop(entry.Consol_ID).IsUnserved = true;
        }
      }
    }
  }

  /* Disable button if green stop selected :
  public get IsbrokerShowable(): Stop[] {
    let test = this.currentStopsValue.StopsList.find(
      (x) => x.IsSelected === true && x.DeliveryMandatoryGreen == true
    );
    // If we find A green parcel selected in the stop list, return empty stop array. So the button broker doesn't appear
    if(test){
      return new Array<Stop>(); 
    }else{
      return this.currentStopsValue.StopsList.filter(
        (x) => x.IsSelected === true
      );
    }

  }
*/

  public GetStop(Consol_ID: number): Stop {
    return this.currentStopsValue.StopsList.find(
      (x) => x.Consol_ID === Consol_ID
    );
  }

  public GetNonCompletedStops(sectorID: number) {
    this.apiProvider.GetNonCompletedStops(sectorID).subscribe(
      (stops: Stops) => {
        this.nonCompletedStopsSubject = new BehaviorSubject<Stops>(stops);
        this.nonCompletedStops = this.nonCompletedStopsSubject.asObservable();
        this.ncDataSource = new MatTableDataSource<Stop>(
          this.getNonCompletedStops.StopsList.filter((s) => s.TotalParcel != 0)
        );
      },
      (err: HttpErrorResponse) => {
        this.nonCompletedStopsSubject.next(null);
      }
    );
  }

  public GetStopFromParcel(ParcelID: number): Stop {
    return this.currentStopsValue.StopsList.find((x) =>
      x.ParcelCompress.find((y) => y.Parcel_ID === ParcelID)
    );
  }

  public GetParcel(ParcelID: number): Parcel {
    this.currentStop = this.GetStopFromParcel(ParcelID);
    if (this.currentStop == null) return null;
    this.currentParcel = this.currentStop.ParcelCompress.find(
      (x) => x.Parcel_ID === ParcelID
    );
    return this.currentParcel;
  }

  public GetStopCountPerStatus(Live_Route_Status: number): number {
    return this.currentStopsValue.StopsList.filter((x) =>
      x.ParcelCompress.find((y) => y.Live_Route_Status >= Live_Route_Status)
    ).length;
  }

  public ChangeParcelStatus(
    ParcelID: number,
    LiveRouteStatus: number
  ): boolean {
    //pas super clair mais je larrange bientot
    this.currentParcel = this.GetParcel(ParcelID);
    if (this.currentParcel.Live_Route_Status < LiveRouteStatus) {
      this.currentParcel.Live_Route_Status = LiveRouteStatus;
      this.currentStop.TotalScanned++;
      this.currentStopsValue.TotalScannedCount++;
      return true;
    } else return false;
  }

  async initialiseRoute() {
    this.GetDataFromDataBase();
  }

  private SelectedDateEqualsToToday() {
    return (
      this.datePipe.transform(this.RouteDate, 'yyyy-MM-dd') ===
      this.datePipe.transform(new Date(), 'yyyy-MM-dd')
    );
  }

  public ResetService() {
    const EmptyList: Stop[] = [];

    if (this.currentStopsValue != null) {
      // commit
      this.currentStopsValue.StopsList = EmptyList;
      this.currentStopsSubject = new BehaviorSubject<Stops>(
        this.currentStopsValue
      );
      this.currentStops = this.currentStopsSubject.asObservable();
      this.dataSource = new MatTableDataSource<Stop>(EmptyList);
    }
  }

  public GetDataFromDataBase(
    GeolocationErrorList?: Stop[],
    UnservedList?: Stop[]
  ) {
    const that = this;
    if (GeolocationErrorList == undefined) {
      GeolocationErrorList = [];
    }
    if (UnservedList == undefined) {
      UnservedList = [];
    }
    if (this.RouteID > 0) {
      this.apiProvider
        .getStopsParcels(
          this.RouteID,
          this.minLiveRouteStatus,
          LiveRouteStatus.Successful,
          this.datePipe.transform(this.RouteDate, 'yyyy-MM-dd')
        )
        .subscribe(
          (stops: Stops) => {
            this.selectedRouteTotalDistance = stops.TotalKm;
            this.currentStopsSubject = new BehaviorSubject<Stops>(stops);
            this.currentStops = this.currentStopsSubject.asObservable();
            this.dataSource = new MatTableDataSource<Stop>(
              this.currentStopsValue.StopsList
            );
            this.googleMapService.setPolyline(stops.RoutePolylines, stops.RoutePolylinesHere);
            this.setLoadingInProgress(false);
          },
          (err: HttpErrorResponse) => {
            this.setLoadingInProgress(false);
            this.currentStopsSubject.next(null);
          }
        );
      /* setTimeout(() => {
        this.verifyData(this.CurrentDataRoot.StopsList);
      }, 1000);*/
    } else {
      //this.ResetService();
      this.setLoadingInProgress(false);
    }
  }

  private snapshotToArray(snapshot) {
    const returnArr = [];

    for (const key in snapshot) {
      if (snapshot[key]) {
        const value = snapshot[key];
        returnArr.push(value);
      }
    }

    return returnArr;
  }

  private RecreateMarker(Stopinfo: Stop): Stop {
    const newaddress: Address = new Address();
    const newmarker: Stop = new Stop();
    const parcelcom: Parcel[] = [];

    newaddress.Address1 = String(Stopinfo.Address.Address1);
    newaddress.Address2 = String(Stopinfo.Address.Address2);
    newaddress.City = String(Stopinfo.Address.City);
    newaddress.Latitude = Number(Stopinfo.Address.Latitude);
    newaddress.Longitude = Number(Stopinfo.Address.Longitude);
    newaddress.Postal_Code = String(Stopinfo.Address.Postal_Code);
    newaddress.Phone = String(Stopinfo.Address.Phone);

    newmarker.Route_ID = Stopinfo.Route_ID;
    newmarker.Lr_Account_ID = String(Stopinfo.Lr_Account_ID);
    newmarker.Lr_Name = String(Stopinfo.Lr_Name);
    newmarker.CustomerName = String(Stopinfo.CustomerName);
    newmarker.Lr_Phone = String(Stopinfo.Lr_Phone);
    newmarker.MaxCOD_TYPE = Number(Stopinfo.MaxCOD_TYPE);
    newmarker.No_Stop = Number(Stopinfo.No_Stop);
    newmarker.Min_Live_Route_Status = Number(Stopinfo.Min_Live_Route_Status);
    newmarker.Max_Live_Route_Status = Number(Stopinfo.Max_Live_Route_Status);
    newmarker.Forecasted_Arrival_Time = Stopinfo.Forecasted_Arrival_Time;
    newmarker.Consol_ID = Stopinfo.Consol_ID;
    newmarker.Address = newaddress;
    newmarker.Type = Stopinfo.Type;

    for (let i = 0; i < Stopinfo.ParcelCompress.length; i++) {
      const onenewparcel: Parcel = new Parcel();
      onenewparcel.CODAmount = Number(Stopinfo.ParcelCompress[i].CODAmount);
      onenewparcel.CODType = String(Stopinfo.ParcelCompress[i].CODType);
      onenewparcel.Dangerous = Stopinfo.ParcelCompress[i].Dangerous;
      onenewparcel.Frozen_Protection =
        Stopinfo.ParcelCompress[i].Frozen_Protection;
      onenewparcel.Live_Route_Status = Number(
        Stopinfo.ParcelCompress[i].Live_Route_Status
      );
      onenewparcel.Live_Route_Type = Number(
        Stopinfo.ParcelCompress[i].Live_Route_Type
      );
      onenewparcel.No_Stop = Number(Stopinfo.ParcelCompress[i].No_Stop);
      onenewparcel.Parcel_ID = Number(Stopinfo.ParcelCompress[i].Parcel_ID);
      onenewparcel.Service_10H30 = Stopinfo.ParcelCompress[i].Service_10H30;
      parcelcom.push(onenewparcel);
    }

    newmarker.ParcelCompress = parcelcom;
    newmarker.Shipping_ID = Number(Stopinfo.Shipping_ID);
    newmarker.TotalCODAmount = Number(Stopinfo.TotalCODAmount);
    newmarker.TotalFrozen_Protection = Stopinfo.TotalFrozen_Protection;
    newmarker.TotalParcel = Number(Stopinfo.TotalParcel);
    newmarker.TotalPickup = Number(Stopinfo.TotalPickup);
    newmarker.TotalScanned = Number(Stopinfo.TotalScanned);
    newmarker.TotalService_10H30 = Number(Stopinfo.TotalService_10H30);
    return newmarker;
  }

  public getPrintRoutePDF(routeId: number, date: Date): Observable<Blob> {
    return this.apiProvider.getPrintRoutePDF(routeId, date);
  }
}
